import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getCurrentCountryCode, getPlatform } from 'selectors/ui/ui';
import type {
  OtpChannel,
  OtpContext,
  SendOtpParams,
} from 'services/payex/otp/types';

type BuildSendOtpPayloadParams = {
  email: string;
  phone?: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  channel: OtpChannel;
};

const useSendOtpPayload = () => {
  const platform = useSelector(getPlatform);
  const recipientId = useSelector(getRecipientId);
  const countryFrom = useSelector(getCurrentCountryCode) as string;
  const countryTo = useSelector(
    (state: RootState) => state.entities?.recipient?.country,
  );

  return useCallback(
    ({
      email,
      phone,
      senderName,
      recipientName,
      context,
      channel,
    }: BuildSendOtpPayloadParams): SendOtpParams => {
      const payload = {
        email,
        ...(phone && { phone }),
        senderName,
        context,
        channel,
        platform,
        ...(recipientId && { portalCode: recipientId }),
        ...(countryFrom && { countryFrom }),
        ...(countryTo && { countryTo }),
      };
      if (context === 'payer_information' && recipientName) {
        return { ...payload, recipientName };
      }
      return payload;
    },
    [platform, recipientId, countryFrom, countryTo],
  );
};

export type { BuildSendOtpPayloadParams };
export { useSendOtpPayload };
